import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Loader2, ScrollText, Search, RefreshCw } from "lucide-react";

const ACTION_COLORS = {
    "parent.create": "bg-green-500/15 text-green-700 dark:text-green-300",
    "parent.update": "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    "parent.delete": "bg-red-500/15 text-red-700 dark:text-red-300",
    "parent.resend_otp": "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    "parents.bulk_upload": "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
    "notification.create": "bg-purple-500/15 text-purple-700 dark:text-purple-300",
    "notification.update": "bg-purple-500/15 text-purple-700 dark:text-purple-300",
    "notification.delete": "bg-red-500/15 text-red-700 dark:text-red-300",
    "album.create": "bg-pink-500/15 text-pink-700 dark:text-pink-300",
    "album.delete": "bg-red-500/15 text-red-700 dark:text-red-300",
    "photo.upload": "bg-pink-500/15 text-pink-700 dark:text-pink-300",
    "photo.delete": "bg-red-500/15 text-red-700 dark:text-red-300",
    "document.upload": "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
    "document.delete": "bg-red-500/15 text-red-700 dark:text-red-300",
    "digest.sent": "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
    "digest.skipped": "bg-muted text-muted-foreground",
};

export default function AdminAuditLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState("");
    const [actor, setActor] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (action) params.action = action;
        if (actor) params.actor = actor;
        const r = await api.get("/admin/audit-logs", { params });
        setLogs(r.data.logs);
        setLoading(false);
    }, [action, actor]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="admin-audit-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Compliance</div>
                    <h1 className="font-heading text-3xl font-black tracking-tight mt-1">Audit Log</h1>
                    <p className="text-muted-foreground text-sm mt-1">Track every administrative action across the portal.</p>
                </div>
                <Button onClick={load} variant="outline" className="rounded-full" data-testid="audit-refresh-btn">
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
            </div>

            <Card className="rounded-2xl">
                <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Filter by action (e.g. parent.delete)"
                                value={action}
                                onChange={(e) => setAction(e.target.value)}
                                className="pl-10 h-11 rounded-full"
                                data-testid="audit-action-filter"
                            />
                        </div>
                        <Input
                            placeholder="Filter by actor (admin username)"
                            value={actor}
                            onChange={(e) => setActor(e.target.value)}
                            className="h-11 rounded-full sm:w-64"
                            data-testid="audit-actor-filter"
                        />
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-16">
                            <ScrollText className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">No audit entries match.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {logs.map((l) => (
                                <div key={l.id} className="px-5 py-3 flex flex-col md:flex-row md:items-center gap-2" data-testid={`audit-row-${l.id}`}>
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <Badge className={`rounded-full text-xs font-mono ${ACTION_COLORS[l.action] || "bg-muted text-muted-foreground"}`}>
                                            {l.action}
                                        </Badge>
                                        <span className="text-sm font-medium truncate">{l.actor}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground md:max-w-md truncate font-mono">
                                        {Object.entries(l.meta || {}).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join("  ")}
                                    </div>
                                    <div className="text-xs text-muted-foreground shrink-0">
                                        {new Date(l.created_at).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
